/**
 * The server's answer when OpenAI refused an AI call because the org has no
 * credits left (2026-11-02): HTTP 503 `{ error: 'ai_unavailable' }` on a JSON
 * endpoint, an `{"error":"ai_unavailable"}` event on an SSE stream
 * (server/app/Support/OpenAi/AiUnavailable.php). Every AI surface shows
 * AI_UNAVAILABLE_MESSAGE for it instead of its generic "try again": trying
 * again will not help until someone tops up.
 */
export const AI_UNAVAILABLE = 'ai_unavailable' as const;

export const AI_UNAVAILABLE_MESSAGE =
  'Our giraffe is out of leaves! The AI tools are taking a short break while we restock and look into it. Please check back soon.';

/** An Error carrying the code, for stream readers that throw on an error event. */
export class AiUnavailableError extends Error {
  readonly code = AI_UNAVAILABLE;

  constructor() {
    super(AI_UNAVAILABLE_MESSAGE);
    this.name = 'AiUnavailableError';
  }
}

/**
 * True for the credits refusal in any shape a caller holds: an axios error
 * (`response.data.error`), an AiUnavailableError, or a parsed body or SSE
 * event (`{ error: 'ai_unavailable' }`).
 */
export function isAiUnavailable(error: unknown): boolean {
  if (error instanceof AiUnavailableError) return true;
  if (!error || typeof error !== 'object') return false;
  const e = error as { error?: unknown; response?: { data?: { error?: unknown } } };
  return e.error === AI_UNAVAILABLE || e.response?.data?.error === AI_UNAVAILABLE;
}
